/**
 * WorkspaceContext — Phase 1 Stub / Phase 2 Foundation
 *
 * Phase 1: Provides workspace config derived from the user's role.
 *          Replaces the local viewAs state in AppShell.
 *          Admin accounts can preview other workspaces.
 *
 * Phase 2: Will support full workspace switching — swapping nav,
 *          dashboard, permissions, and data layer simultaneously
 *          without a page reload.
 *
 * Consumers:
 *   const { workspace, previewAs, setPreviewAs } = useWorkspace()
 */
import React, {
  createContext, useContext, useState, useCallback, useMemo
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  WORKSPACES, ADMIN_PREVIEWABLE_WORKSPACES,
  getWorkspaceForRole,
  type WorkspaceConfig, type WorkspaceId,
} from '@/lib/workspace'

interface WorkspaceContextValue {
  /** The workspace the current user actually belongs to (from their role) */
  userWorkspace:   WorkspaceConfig
  /** The workspace currently being displayed (may differ if admin is previewing) */
  workspace:       WorkspaceConfig
  /** Whether the admin is currently previewing a different workspace */
  isPreviewing:    boolean
  /** List of workspace IDs this user can switch to / preview */
  availableWorkspaces: WorkspaceId[]
  /** Preview a workspace (admin only). Pass null to reset to own workspace. */
  setPreviewAs:    (id: WorkspaceId | null) => void
  /** The workspace ID being previewed, or null */
  previewingAs:    WorkspaceId | null
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, isAdmin } = useAuth()
  const [previewingAs, setPreviewingAs] = useState<WorkspaceId | null>(null)

  const userWorkspace = useMemo(() => {
    if (!profile) return WORKSPACES.staff
    return getWorkspaceForRole(profile.role, isAdmin)
  }, [profile, isAdmin])

  const workspace = useMemo(() => {
    if (isAdmin && previewingAs && WORKSPACES[previewingAs]) {
      return WORKSPACES[previewingAs]
    }
    return userWorkspace
  }, [isAdmin, previewingAs, userWorkspace])

  const availableWorkspaces = useMemo(
    () => isAdmin ? ADMIN_PREVIEWABLE_WORKSPACES : [userWorkspace.id],
    [isAdmin, userWorkspace.id]
  )

  const setPreviewAs = useCallback((id: WorkspaceId | null) => {
    if (!isAdmin) return   // Only admins can preview other workspaces
    setPreviewingAs(id)
  }, [isAdmin])

  const isPreviewing = isAdmin && previewingAs !== null && previewingAs !== userWorkspace.id

  return (
    <WorkspaceContext.Provider value={{
      userWorkspace,
      workspace,
      isPreviewing,
      availableWorkspaces,
      setPreviewAs,
      previewingAs,
    }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used inside WorkspaceProvider')
  return ctx
}
